import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';
import MediaQuery from 'react-responsive';
import { RemoveScroll } from 'react-remove-scroll';

import { fetchCategoryList } from 'tradera-state/categories/actions';
import uiActions from 'tradera-state/ui/actions';
import { BREAKPOINTS, BREAKPOINT_SIZES } from 'tradera-constants/breakpoints';
import { logger } from 'packages/logger';
import GtmService from 'static/script/app/ui/google-tagmanager-service';
import {
    EnterFade,
    EnterFrom
} from 'tradera-components/transitions/transitions';
import ApplicationErrorBoundary from 'tradera-components/application-error-boundary/application-error-boundary';
import Overlay from 'components/modals/overlay';
import DropDownMenuHamburger from './dropdown-menu-burger';
import DropdownMenuContent from './dropdown-menu-content';
import { SeoLinks } from './seo-links';

const trackAnalytics = label => {
    try {
        GtmService.trackAction('Top navigation', 'Category menu', label);
    } catch (error) {
        logger(error);
    }
};

const DropdownMenu = ({
    className,
    inSPA,
    isLoggedIn,
    transparentHeader,
    tabIndex
}) => {
    const dropdownMenu = useSelector(state => state.ui.categoryDropdownMenu);
    const categories = useSelector(state => state.categories);
    const expanded = dropdownMenu.open === 'open';
    const dispatch = useDispatch();

    const onClick = () => {
        if (expanded) {
            dispatch(uiActions.closeCategoryDropdownMenu());
            trackAnalytics('Menu close');
        } else {
            dispatch(uiActions.closeProfileMenu());
            if (!categories.isFetching && categories.list.length === 0) {
                dispatch(fetchCategoryList());
            }
            dispatch(uiActions.openCategoryDropdownMenu());

            trackAnalytics('Menu open');
        }
    };

    const onItemClick = () => {
        dispatch(uiActions.closeCategoryDropdownMenu());
    };

    const sideMenuMediaProps = {
        maxWidth: BREAKPOINT_SIZES[BREAKPOINTS.LG].max
    };
    const dropdownMenuMediaProps = {
        minWidth: BREAKPOINT_SIZES[BREAKPOINTS.LG].max
    };

    const renderChild = () => (
        <RemoveScroll forwardProps>
            <div className={`site-dropdown mt-xl-1`} tabIndex="-1">
                <DropdownMenuContent
                    categories={categories}
                    inSPA={inSPA}
                    isLoggedIn={isLoggedIn}
                    onClick={onItemClick}
                />
            </div>
        </RemoveScroll>
    );

    return (
        <ApplicationErrorBoundary>
            <nav className={className} role="navigation">
                <DropDownMenuHamburger
                    active={expanded}
                    className="site-dropdown__hamburger ml-md-1 ml-lg-0 ml-xl-0"
                    onClick={() => {
                        onClick();
                    }}
                    tabIndex={tabIndex}
                />
                <SeoLinks topLevelCategories={categories?.topLevelCategories} />
                <TransitionGroup component={null} mountOnEnter>
                    {expanded && (
                        <EnterFrom from="left" in={expanded}>
                            <MediaQuery {...sideMenuMediaProps}>
                                {renderChild()}
                            </MediaQuery>
                        </EnterFrom>
                    )}
                </TransitionGroup>
                {expanded && (
                    <MediaQuery {...dropdownMenuMediaProps}>
                        {renderChild()}
                    </MediaQuery>
                )}
            </nav>
            <TransitionGroup component={null} mountOnEnter>
                {expanded && (
                    <EnterFade in={expanded}>
                        <Overlay
                            style={{
                                zIndex: 4,
                                top: transparentHeader ? '0' : '3.5rem',
                                height: transparentHeader
                                    ? '100vh'
                                    : 'calc(100vh - 3.5em)'
                            }}
                            show={expanded}
                            closeModalCallback={() => {
                                onClick();
                            }}
                            tabIndex="-1"
                        />
                    </EnterFade>
                )}
            </TransitionGroup>
        </ApplicationErrorBoundary>
    );
};

DropdownMenu.propTypes = {
    className: PropTypes.string,
    isLoggedIn: PropTypes.bool,
    expanded: PropTypes.bool
};

DropdownMenu.defaultProps = {
    className: ''
};

export default DropdownMenu;
